/**
 * LookupCombo — the XAF lookup control used by every Sample Registration field
 * whose value comes from a reference list (prototype: `openPicker` / `gpick`).
 *
 * Two behaviours, one component:
 *   · single-select — click opens a multi-COLUMN grid popup with a filter box;
 *     picking a row closes it and returns the whole row (so the caller can fill
 *     dependent read-only fields, e.g. Client → Address + Phone).
 *   · CheckedComboBox (`multi`) — the popup is a checkbox list that STAYS OPEN
 *     while you tick, and the closed field shows the picks semicolon-joined
 *     ("Water; Solid"), exactly like the legacy XAF CheckedComboBox.
 *
 * Optionally renders the inline green "＋" quick-add (legacy: "Select a client
 * from the dropdown; Allow to add a new one") — the host owns the dialog.
 *
 * Two XAF behaviours live HERE, in the control, never in the data source:
 *   · the "N/A" null option — a single-select lookup always offers N/A as its
 *     FIRST row, and shows N/A in the closed field while nothing is chosen.
 *     Picking it clears the field AND its dependants.  No stored procedure ever
 *     returns an N/A row (user ruling 2026-07-29).
 *   · a SINGLE-column lookup renders no column caption — the header row only
 *     earns its space when there is more than one column to tell apart.
 *   · the inline "⊗" CLEAR button (legacy XAF lookup editor) — appears in the
 *     field only once it HAS a value, and wipes it without opening the list.
 *     Same effect as picking N/A, so dependants clear with it.
 *
 * Why local to this feature and not in `components/`: the two shared combos are
 * bound to their own APIs (`QueryCombo` → module column-distinct, `EddGridCombo`
 * → the EDD filter-grid endpoint), so neither can serve a static multi-column
 * list.  Promote this to `components/` when the Sample Registration lookup
 * endpoints land and a second form needs the same control.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SrLookup } from './srData';

/** Popup auto-sizes to fit its content (CSS width:max-content) but is never
 *  narrower than the trigger cell. When popupMatchWidth is set, the popup is
 *  locked to the trigger width instead. */

export interface LookupComboProps {
  /** Column captions + rows; column 0 is the value. */
  lookup: SrLookup;
  /** Single-select: the chosen value ('' = none).  Ignored when `multi`. */
  value?: string;
  /** CheckedComboBox: the ticked values. */
  values?: string[];
  multi?: boolean;
  disabled?: boolean;
  /** Allow the dropdown to open (view selections) but disable all checkboxes
   *  so nothing can be added or removed. Hides the clear button too. */
  readOnlyItems?: boolean;
  placeholder?: string;
  /** Single-select pick — receives the FULL row so dependants can be filled. */
  onPick?: (row: string[]) => void;
  /** CheckedComboBox toggle — receives the next full selection. */
  onToggle?: (next: string[], toggled: string[]) => void;
  /** Renders the inline green ＋; called when it is clicked. */
  onNew?: () => void;
  newTitle?: string;
  /** Blocked reason — clicking warns instead of opening (e.g. "pick a matrix"). */
  blockedReason?: string;
  onBlocked?: (reason: string) => void;
  /** Extra class on the trigger (e.g. the missing-required flash). */
  className?: string;
  id?: string;
  /** Derives the STORED value / closed-field label for a row, separate from the
   *  displayed grid columns. Defaults to column 0. The Test picker passes a
   *  "TestName|MethodNumber" composite (XAF parity) so same-named tests are
   *  distinct while the grid still shows Test and Method in separate columns. */
  rowValue?: (row: string[]) => string;
  /** Force the popup to exactly match the trigger width (sets inline min-width =
   *  trigger width, overriding the CSS min-width). Use on narrow triggers inside
   *  dialogs where the 520 px CSS floor would make the popup too wide. */
  popupMatchWidth?: boolean;
  /** Override the scrollable list area height (px). Bypasses both the CSS
   *  max-height default and the modal geometry clamp. Use to pin visible rows. */
  listMaxHeight?: number;
}

export function LookupCombo({
  lookup, value = '', values = [], multi = false, disabled = false,
  readOnlyItems = false,
  placeholder = 'N/A', onPick, onToggle, onNew, newTitle,
  blockedReason, onBlocked, className, id,
  rowValue = (row) => row[0] ?? '',
  popupMatchWidth = false,
  listMaxHeight,
}: LookupComboProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxH?: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  // A stable <div> container for the popup portal. It is appended to the END
  // of document.body whenever the popup opens, ensuring it is always painted
  // AFTER the dialog overlay (<div class="ad-overlay"> which is also a body
  // portal). This guarantees DOM-order stacking wins even when z-index alone
  // isn't enough (e.g. flex / overflow stacking context quirks on .ad-modal).
  const portalNode = useRef<HTMLDivElement | null>(null);
  if (!portalNode.current) {
    portalNode.current = document.createElement('div');
    portalNode.current.setAttribute('data-sr-pick-root', '');
  }
  useEffect(() => {
    if (open && portalNode.current) {
      // appendChild moves it to end of body if already attached; appends if not.
      document.body.appendChild(portalNode.current);
    }
  }, [open]);
  useEffect(() => {
    // Remove the container on component unmount to avoid leaking DOM nodes.
    return () => { portalNode.current?.remove(); };
  }, []);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = r.width;
    const pad = 8;
    // chrome = filter-bar (~38px) + status-footer (~28px) inside the popup.
    const chrome = 66;
    const wantH = listMaxHeight !== undefined ? listMaxHeight : 280;

    // The popup is position:fixed z-index:9000 portaled into a dedicated node
    // that is always appended LAST to document.body when it opens (see portalNode
    // above). This ensures it paints on top of the dialog overlay (z-index:1500)
    // regardless of any stacking-context quirks from flex/overflow on .ad-modal.
    // Fixed-position children are NOT clipped by overflow on any ancestor.
    // Therefore we ONLY care about viewport space, not dialog bounds.
    //
    // Policy: always show BELOW the trigger (users always expect a dropdown
    // to open downward). Only flip ABOVE if there is genuinely too little
    // viewport space below (< 120px) AND at least 2× more room above.
    const spaceBelow = window.innerHeight - r.bottom - pad;
    const spaceAbove = r.top - pad;
    const left = Math.max(pad, Math.min(r.left, window.innerWidth - width - pad));

    if (spaceBelow >= 120 || spaceBelow >= spaceAbove / 2) {
      // Show below the trigger.
      const maxH = Math.max(60, Math.min(spaceBelow - chrome - pad, wantH));
      setPos({ top: r.bottom + 2, left, width, maxH });
    } else {
      // Flip above — genuinely more room there.
      const maxH = Math.max(60, Math.min(spaceAbove - chrome - pad, wantH));
      const h = chrome + maxH;
      setPos({ top: r.top - h - 2, left, width, maxH });
    }
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const reanchor = (e: Event) => {
      // Scrolling inside the popup's own list shouldn't reposition it.
      if (popRef.current?.contains(e.target as Node)) return;
      place();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', reanchor, true);
    window.addEventListener('resize', reanchor);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', reanchor, true);
      window.removeEventListener('resize', reanchor);
    };
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => filterRef.current?.focus(), 20); }, [open]);

  // When the popup opens, scroll the first currently-selected row into view so
  // the user starts on their pick (matching XAF's lookup grid). Runs AFTER the
  // filter-focus timeout so the pop is fully laid out. Passive — no-op when
  // nothing is selected (fresh N/A field).
  const firstSelectedRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      firstSelectedRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
    }, 40);
    return () => clearTimeout(id);
  }, [open]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lookup.rows;
    return lookup.rows.filter(r => r.join(' ').toLowerCase().includes(q));
  }, [lookup.rows, query]);

  // Index of the first VISIBLE row that's currently selected — used both to
  // scroll it into view on open and to give it the accent highlight.
  const firstSelectedIndex = useMemo(() => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row) continue;
      const v = multi ? rowValue(row) : (row[0] ?? '');
      if (multi ? values.includes(v) : v === value) return i;
    }
    return -1;
  }, [rows, multi, values, value, rowValue]);

  // Re-clamp `left` after the popup renders at its content width so it stays
  // inside the viewport (the popup now auto-sizes to content).
  const clampedRef = useRef(false);
  useLayoutEffect(() => {
    if (!open) { clampedRef.current = false; return; }
    if (clampedRef.current || !popRef.current || !pos) return;
    clampedRef.current = true;
    const el = popRef.current;
    const pad = 8;
    const realW = el.offsetWidth;
    const maxLeft = window.innerWidth - realW - pad;
    if (pos.left > maxLeft && maxLeft >= pad) {
      setPos(p => p ? { ...p, left: maxLeft } : p);
    }
  });

  // A single-column list needs no caption — the field's own label already says
  // what it is, and a lone header just wastes a row.
  const showHeader = lookup.cols.length > 1;

  // The XAF null option.  Picking it hands the host a row of empty strings, so
  // `applyPick` clears the field AND every dependant it fills (Client → Address
  // + Phone), exactly as picking a real row would set them.
  const naRow = useMemo(() => lookup.cols.map(() => ''), [lookup.cols]);

  const toggleTrigger = () => {
    if (disabled) return;
    if (blockedReason) { onBlocked?.(blockedReason); return; }
    setQuery('');
    setOpen(o => !o);
  };

  const pick = (row: string[]) => {
    if (readOnlyItems) return;
    if (multi) {
      const v = rowValue(row);
      const next = values.includes(v) ? values.filter(x => x !== v) : [...values, v];
      onToggle?.(next, row);
      return;   // CheckedComboBox stays open
    }
    onPick?.(row);
    setOpen(false);
  };

  const allValues = useMemo(() => rows.map(r => rowValue(r)), [rows, rowValue]);
  const allSelected = allValues.length > 0 && allValues.every(v => values.includes(v));

  const toggleAll = () => {
    if (readOnlyItems) return;
    if (allSelected) {
      // Uncheck all visible rows (keep any values from rows hidden by filter)
      const visibleSet = new Set(allValues);
      onToggle?.(values.filter(v => !visibleSet.has(v)), []);
    } else {
      // Check all visible rows (merge with any already-selected hidden values)
      const merged = [...new Set([...values, ...allValues])];
      onToggle?.(merged, []);
    }
  };

  const label = multi ? values.join('; ') : value;

  /** Wipe the field without opening the list.  Single-select routes through the
   *  same empty row the N/A option uses, so dependants (Client → Address +
   *  Phone) clear too; a CheckedComboBox simply unticks everything. */
  const clear = () => {
    if (multi) onToggle?.([], []);
    else onPick?.(naRow);
  };

  return (
    <div className="sr-combo">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`sr-combo-v${className ? ` ${className}` : ''}`}
        disabled={disabled}
        onClick={toggleTrigger}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label
          ? (multi
            ? <span className="sr-combo-val" title={label}>{label}</span>
            : <span className="sr-combo-tag" title={label}>{label}</span>)
          : <span className="sr-combo-ph">{placeholder}</span>}
        {/* Inline clear — only once there is something to clear, and never on a
            read-only or readOnlyItems field.  `role="button"` (not <button>)
            because this sits INSIDE the trigger button and nested buttons are
            invalid HTML. */}
        {label && !disabled && !readOnlyItems && (
          <span
            className="sr-combo-clear"
            role="button"
            tabIndex={-1}
            aria-label="Clear"
            title="Clear"
            onClick={(e) => { e.stopPropagation(); clear(); }}
          >
            <i className="ti ti-circle-x" aria-hidden="true" />
          </span>
        )}
        {onNew && (
          <span
            className="sr-combo-new"
            role="button"
            tabIndex={-1}
            title={newTitle ?? 'New'}
            onClick={(e) => { e.stopPropagation(); onNew(); }}
          >＋</span>
        )}
        <span className="sr-combo-caret" aria-hidden="true" />
      </button>

      {open && pos && createPortal(
        <div
          className="sr-pick"
          ref={popRef}
          style={{
            top: pos.top, left: pos.left,
            // Let the popup auto-size to fit content (CSS width:max-content)
            // but never narrower than the trigger cell.
            ...(popupMatchWidth
              ? { width: pos.width, minWidth: pos.width }
              : { minWidth: pos.width }),
          }}
          role="listbox"
        >
          <div className="sr-pick-f">
            <input
              ref={filterRef}
              placeholder="Filter…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="sr-pick-wrap" style={{
            maxHeight: listMaxHeight !== undefined
              ? listMaxHeight
              : pos.maxH ?? undefined,
          }}>
            <table className="sr-pick-table">
              {showHeader && (
                <thead>
                  <tr>
                    {multi && <th className="sr-pick-cb" />}
                    {lookup.cols.map(c => <th key={c}>{c}</th>)}
                  </tr>
                </thead>
              )}
              <tbody>
                {/* N/A — scrolls with the list (no sticky); first row so it
                    is always at the top when the popup first opens. */}
                {!multi && (
                  <tr
                    className={`sr-pick-na${value ? '' : ' on'}`}
                    onClick={() => pick(naRow)}
                  >
                    <td colSpan={lookup.cols.length}>N/A</td>
                  </tr>
                )}
                {multi && rows.length > 0 && (
                  <tr
                    className={`sr-pick-selall${allSelected ? ' on' : ''}`}
                    onClick={readOnlyItems ? undefined : toggleAll}
                  >
                    <td className="sr-pick-cb">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={readOnlyItems}
                        onChange={readOnlyItems ? undefined : toggleAll}
                        onClick={e => e.stopPropagation()}
                        aria-label="Select All"
                      />
                    </td>
                    <td colSpan={lookup.cols.length}>(Select All)</td>
                  </tr>
                )}
                {rows.length === 0 ? (
                  <tr>
                    <td className="sr-pick-empty" colSpan={lookup.cols.length + (multi ? 1 : 0)}>
                      No matches.
                    </td>
                  </tr>
                ) : rows.map((r, i) => {
                  const v = multi ? rowValue(r) : (r[0] ?? '');
                  const on = multi ? values.includes(v) : v === value;
                  // Ref + accent-highlight the FIRST selected VISIBLE row so
                  // the open-effect can scroll to it (XAF parity).
                  const firstSel = on && i === firstSelectedIndex;
                  return (
                    <tr key={`${v}-${i}`}
                        ref={firstSel ? firstSelectedRef : undefined}
                        className={on ? (firstSel ? 'on on-first' : 'on') : ''}
                        onClick={() => pick(r)}>
                      {multi && (
                        <td className="sr-pick-cb">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={readOnlyItems}
                            onChange={readOnlyItems ? undefined : () => pick(r)}
                            onClick={e => e.stopPropagation()}
                            aria-label={v}
                          />
                        </td>
                      )}
                      {/* Render only the caption columns; any trailing row
                          elements (e.g. a hidden Oid the caller carries for
                          exact resolution) stay off-screen. */}
                      {r.slice(0, lookup.cols.length).map((c, ci) => <td key={ci}>{c}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sr-pick-g">
            <span>
              {multi ? `${values.length} selected · ${rows.length} items` : `${rows.length} items`}
            </span>
            {onNew && (
              <button className="ca-btn" style={{ padding: '2px 9px', fontSize: 11 }}
                onClick={() => { setOpen(false); onNew(); }}>
                <span className="tb-i tb-i-green">＋</span> New
              </button>
            )}
          </div>
        </div>,
        portalNode.current!,
      )}
    </div>
  );
}
